import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { getNotifications, getUnreadCount, markAsRead, markAllAsRead, Notification } from '../lib/api/notifications';
import { supabase } from '../lib/supabaseClient';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  refreshNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  addNotification: (notification: Notification) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refresh notifications
  const refreshNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [notificationsResult, unreadResult] = await Promise.all([
        getNotifications({ limit: 50 }),
        getUnreadCount()
      ]);

      if (notificationsResult.error) {
        setError(notificationsResult.error);
      } else {
        setNotifications(notificationsResult.notifications);
      }

      if (unreadResult.error) {
        console.error('Error fetching unread count:', unreadResult.error);
      } else {
        setUnreadCount(unreadResult.count);
      }
    } catch (err) {
      console.error('Error refreshing notifications:', err);
      setError('Неочаквана грешка при зареждане на известията');
    } finally {
      setLoading(false);
    }
  }, []);

  // Mark notification as read
  const handleMarkAsRead = useCallback(async (id: string) => {
    try {
      const result = await markAsRead(id);
      if (result.success) {
        setNotifications(prev => 
          prev.map(notification => 
            notification.id === id 
              ? { ...notification, read_at: new Date().toISOString() }
              : notification
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } else {
        console.error('Error marking notification as read:', result.error);
      }
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }, []);

  // Mark all notifications as read
  const handleMarkAllAsRead = useCallback(async () => {
    try {
      const result = await markAllAsRead();
      if (result.success) {
        setNotifications(prev => 
          prev.map(notification => ({
            ...notification,
            read_at: new Date().toISOString()
          }))
        );
        setUnreadCount(0);
      } else {
        console.error('Error marking all notifications as read:', result.error);
      }
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }, []);

  // Add notification to local state
  const addNotification = useCallback((notification: Notification) => {
    setNotifications(prev => [notification, ...prev]);
    if (!notification.read_at) {
      setUnreadCount(prev => prev + 1);
    }
  }, []);

  // Load notifications on mount
  useEffect(() => {
    refreshNotifications();
  }, []);

  // Set up real-time subscription for notifications
  useEffect(() => {
    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications'
        },
        (payload: any) => {
          const newNotification = payload.new as Notification;
          addNotification(newNotification);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const value: NotificationContextType = useMemo(() => ({
    notifications,
    unreadCount,
    loading,
    error,
    refreshNotifications,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    addNotification
  }), [notifications, unreadCount, loading, error, refreshNotifications, handleMarkAsRead, handleMarkAllAsRead, addNotification]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
